import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import "@/App.css";
import axios from "axios";
import { 
  Bot, User, Send, Plus, MessageSquare, Image, FileText, 
  Menu, X, Trash2, Sparkles, Upload, Loader2, LogIn, LogOut,
  Mic, MicOff, Volume2, VolumeX, Download, Brain,
  Smile, Briefcase, GraduationCap, Laugh, UserCircle, Shield,
  Coins, Crown, Phone, PhoneOff, Settings, SendHorizontal, ImagePlus, Camera,
  Globe, CloudSun, DollarSign, Newspaper, FolderSearch, ArrowDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Toaster, toast } from "sonner";

// Import i18n
import { useLanguage } from "@/i18n";

// Import context hooks
import { useUI, useChat } from "@/contexts";

// Import modals
import {
  ConfirmDialog,
  AuthModal,
  ProfileModal,
  CreditModal,
  TransferModal,
  SubscriptionModal,
  MemoryPanel,
  PaymentSuccessModal,
  AdminPanel,
  AssistantCreatorModal,
  UpgradeModal,
  VideoStudioModal,
  ExchangeModal,
  PhantomModal
} from "@/components/modals";

// Import chat components
import { Sidebar, WelcomeScreen, ChatHeader, InputArea, ChatMessages, SpeakingWaveform } from "@/components/chat";
import { VoiceModeModal } from "@/components/chat/VoiceModeModal";
import { DeepResearchModal, DeepResearchJobView, AgentLeaderboard, DeepResearchJobsPanel, CustomAgentsModal } from "@/components/deep_research";

// Import feedback and analytics
import FeedbackButtons from "@/components/FeedbackButtons";
import AnalyticsPanel from "@/components/AnalyticsPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Personality icons
const PERSONALITY_ICONS = {
  default: UserCircle,
  friendly: Smile,
  formal: Briefcase,
  funny: Laugh,
  teacher: GraduationCap
};

// Auth Hook
const useAuth = () => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("kaan_token"));

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch {
      logout();
    }
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("kaan_token", res.data.token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.token}`;
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (email, password, name) => {
    const res = await axios.post(`${API}/auth/register`, { email, password, name });
    localStorage.setItem("kaan_token", res.data.token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.token}`;
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("kaan_token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
  };

  return { user, setUser, token, login, register, logout };
};

function App() {
  const { user, setUser, login, register, logout } = useAuth();
  const { t, language: currentLang } = useLanguage();
  
  // Get all modal states from UIContext
  const {
    // Confirm Dialog
    showConfirmDialog,
    setShowConfirmDialog,
    confirmConfig,
    showConfirm,
    // Sidebar
    isSidebarOpen,
    setIsSidebarOpen,
    // All Modals
    showAuthModal,
    setShowAuthModal,
    authMode,
    setAuthMode,
    showProfileModal,
    setShowProfileModal,
    showCreditModal,
    setShowCreditModal,
    showTransferModal,
    setShowTransferModal,
    showSubscriptionModal,
    setShowSubscriptionModal,
    showMemoryPanel,
    setShowMemoryPanel,
    showAdminPanel,
    setShowAdminPanel,
    showAssistantCreator,
    setShowAssistantCreator,
    showAnalyticsPanel,
    setShowAnalyticsPanel,
    showUpgradeModal,
    setShowUpgradeModal,
    // Theme
    theme: uiTheme,
    toggleTheme,
    closeAllModals
  } = useUI();
  
  // Get chat state from ChatContext
  const {
    conversations,
    setConversations,
    activeConversation,
    setActiveConversation,
    messages,
    setMessages,
    selectedAssistant,
    setSelectedAssistant,
    selectedCustomAssistant,
    setSelectedCustomAssistant,
    customAssistants,
    setCustomAssistants,
    isLoading,
    setIsLoading,
    streamingText,
    setStreamingText,
    mode,
    setMode,
    voiceEnabled,
    setVoiceEnabled,
    goHome: chatGoHome
  } = useChat();
  
  // Local UI state (not shared)
  const [inputValue, setInputValue] = useState("");
  const [imagePrompt, setImagePrompt] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileQuestion, setFileQuestion] = useState("");
  const [personalities, setPersonalities] = useState([]);
  const [selectedPersonality, setSelectedPersonality] = useState("default");
  const [useStreaming] = useState(true);
  const [voiceModeOpen, setVoiceModeOpen] = useState(false);
  
  // Initialize theme from localStorage
  const getInitialTheme = () => {
    try {
      const savedSettings = localStorage.getItem("kaan-ai-settings");
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings);
        let theme = parsed.theme || "dark";
        if (theme === "system") {
          const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
          theme = prefersDark ? "dark" : "light";
        }
        // Apply theme immediately on init
        if (theme === "light") {
          document.documentElement.classList.add("light-theme");
          document.body.classList.add("light-theme");
        } else {
          document.documentElement.classList.remove("light-theme");
          document.body.classList.remove("light-theme");
        }
        return theme;
      }
    } catch (e) {}
    // Default is dark - ensure dark theme CSS is applied
    document.documentElement.classList.remove("light-theme");
    document.body.classList.remove("light-theme");
    return "dark";
  };
  const [currentTheme, setCurrentTheme] = useState(getInitialTheme);
  
  // Anonymous limit state
  const [anonymousLimit, setAnonymousLimit] = useState({ remaining: 5, limit: 5, used: 0 });
  
  // Auth form state (modal visibility comes from UIContext)
  const [authForm, setAuthForm] = useState({ email: "", password: "", name: "", newPassword: "" });
  
  // Admin state (modal visibility comes from UIContext)
  const [adminDashboard, setAdminDashboard] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  
  // Memory state (panel visibility comes from UIContext)
  const [userMemory, setUserMemory] = useState(null);
  const [newFact, setNewFact] = useState("");
  const [newInterest, setNewInterest] = useState("");
  const [birthdayDate, setBirthdayDate] = useState("");
  
  // Image analysis state
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedFaces, setSelectedFaces] = useState([]);
  const [isAnalyzingImage, setIsAnalyzingImage] = useState(false);
  const imageInputRef = useRef(null);
  
  // Image editing state
  const [imageEditMode, setImageEditMode] = useState("generate");
  const [editImageFile, setEditImageFile] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);
  const [editPrompt, setEditPrompt] = useState("");
  const [isEditingImage, setIsEditingImage] = useState(false);
  const editImageInputRef = useRef(null);
  
  // Credit state (modal visibility comes from UIContext)
  const [credits, setCredits] = useState(0);
  const [creditPackages, setCreditPackages] = useState([]);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(null);
  const [isUnlimitedCredits, setIsUnlimitedCredits] = useState(false);
  
  // Subscription state (modal visibility comes from UIContext)
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [userSubscription, setUserSubscription] = useState(null);
  
  // RAG state
  const [ragDocuments, setRagDocuments] = useState([]);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  
  // Voice features (voiceEnabled comes from ChatContext)
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceChatMode, setVoiceChatMode] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState("onyx");
  const [showVideoStudio, setShowVideoStudio] = useState(false);
  const [showExchange, setShowExchange] = useState(false);
  const [showPhantom, setShowPhantom] = useState(false);
  const [swapPrefill, setSwapPrefill] = useState(null);
  // selectedAssistant, customAssistants, selectedCustomAssistant come from ChatContext
  const recognitionRef = useRef(null);
  const currentAudioRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const audioSourceRef = useRef(null);
  const ttsAbortRef = useRef(false);

  // Deep Research
  const [showDeepResearchModal, setShowDeepResearchModal] = useState(false);
  const [drJobViewOpen, setDrJobViewOpen] = useState(false);
  const [drCurrentJobId, setDrCurrentJobId] = useState(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showJobsPanel, setShowJobsPanel] = useState(false);
  const [showCustomAgents, setShowCustomAgents] = useState(false);
  
  const messagesEndRef = useRef(null);
  const chatScrollRef = useRef(null);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  // showConfirm function now comes from UIContext

  // Check for payment return from Stripe
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const isDrReturn = window.location.pathname.startsWith('/deep-research/success');
    if (sessionId && user) {
      if (isDrReturn) {
        // Deep Research: verify payment, then auto-start pending job
        handleDeepResearchReturn(sessionId);
      } else {
        pollPaymentStatus(sessionId);
      }
      window.history.replaceState({}, document.title, '/');
    }
  }, [user]);

  const handleDeepResearchReturn = async (sessionId) => {
    try {
      // Poll status until paid (up to ~30s)
      let paid = false;
      for (let i = 0; i < 15; i++) {
        const st = await axios.get(`${API}/deep-research/status/${sessionId}`);
        if (st.data.payment_status === 'paid') { paid = true; break; }
        await new Promise(r => setTimeout(r, 2000));
      }
      if (!paid) {
        toast.error(t('dr_payment_failed'));
        return;
      }
      const draftRaw = localStorage.getItem('dr_pending_task');
      if (!draftRaw) {
        toast.info('Ödeme başarılı. Görev bilgileri kaybolmuş, lütfen tekrar başlatın.');
        return;
      }
      const draft = JSON.parse(draftRaw);
      localStorage.removeItem('dr_pending_task');
      const res = await axios.post(`${API}/deep-research/start`, {
        task: draft.task, domain: draft.domain, language: draft.language, session_id: sessionId
      });
      toast.success(t('dr_job_started'));
      setDrCurrentJobId(res.data.job_id);
      setDrJobViewOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('dr_error'));
    }
  };

  // Memoized computed values
  const archivedConversations = useMemo(() => 
    conversations.filter(c => c.is_archived), 
    [conversations]
  );
  
  const activeConversations = useMemo(() => 
    conversations.filter(c => !c.is_archived), 
    [conversations]
  );

  // Keep the latest activeConversation in a ref so long-lived callbacks
  // (e.g. voice mode's runTurn) always read the current conv id, not a
  // stale closure value.
  const activeConversationRef = useRef(null);
  useEffect(() => { activeConversationRef.current = activeConversation; }, [activeConversation]);

  // Load data on mount
  useEffect(() => {
    loadConversations();
    loadPersonalities();
    loadCreditPackages();
    loadSubscriptionPlans();
    if (user) {
      loadUserMemory();
      loadCredits();
      loadSubscriptionStatus();
      loadCustomAssistants();
    }
    
    // Apply saved theme on mount
    const savedSettings = localStorage.getItem("kaan-ai-settings");
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        let themeToApply = parsed.theme || "dark";
        if (themeToApply === "system") {
          const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
          themeToApply = prefersDark ? "dark" : "light";
        }
        setCurrentTheme(themeToApply);
        if (themeToApply === "light") {
          document.documentElement.classList.add("light-theme");
          document.body.classList.add("light-theme");
        }
      } catch (e) {
        console.error("Failed to apply theme:", e);
      }
    }
    
    // Listen for theme change events from ProfileModal
    const handleThemeChange = (e) => {
      setCurrentTheme(e.detail);
    };
    window.addEventListener("themeChange", handleThemeChange);
    
    return () => {
      window.removeEventListener("themeChange", handleThemeChange);
    };
  }, [user]);

  useEffect(() => {
    if (activeConversation) {
      loadMessages(activeConversation.id);
      setSelectedPersonality(activeConversation.personality || "default");
    } else {
      setMessages([]);
    }
  }, [activeConversation]);

  // Load anonymous limit for non-logged users
  useEffect(() => {
    if (!user) {
      loadAnonymousLimit();
    }
  }, [user]);

  const loadAnonymousLimit = async () => {
    try {
      const res = await axios.get(`${API}/chat/anonymous-limit`);
      setAnonymousLimit(res.data);
    } catch (error) {
      console.error("Failed to load anonymous limit:", error);
    }
  };

  useEffect(() => {
    const scroller = chatScrollRef.current;
    if (!scroller) {
      messagesEndRef.current?.scrollIntoView();
      return;
    }
    // Only auto-pin to the bottom if the user is already near it. Calling
    // scrollIntoView({smooth}) on every streamed token restarts a smooth
    // animation ~30x/sec → visible "screen shaking", and hijacks the user
    // when they scroll up to read. Instant pin avoids both.
    const nearBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 160;
    if (nearBottom) {
      scroller.scrollTop = scroller.scrollHeight;
      setShowScrollDown(false);
    } else {
      setShowScrollDown(true);
    }
  }, [messages, streamingText]);

  // Toggle the "scroll to bottom" button as the user scrolls the chat.
  useEffect(() => {
    const scroller = chatScrollRef.current;
    if (!scroller) return;
    const onScroll = () => {
      const nearBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 160;
      setShowScrollDown(!nearBottom);
    };
    scroller.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => scroller.removeEventListener("scroll", onScroll);
  }, [activeConversation, messages.length]);

  const scrollToBottom = () => {
    const scroller = chatScrollRef.current;
    if (scroller) scroller.scrollTo({ top: scroller.scrollHeight, behavior: "smooth" });
    setShowScrollDown(false);
  };

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
    }
  }, [inputValue]);

  // Voice Chat Mode - Setup Speech Recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'tr-TR';
      
      recognition.onresult = (event) => {
        let finalTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript;
          }
        }
        if (finalTranscript && voiceChatMode) {
          setInputValue(finalTranscript);
        }
      };
      
      recognition.onerror = (event) => {
        if (event.error === 'no-speech' || event.error === 'aborted') {
          setTimeout(() => {
            if (voiceChatMode) {
              try { recognition.start(); } catch(e) {}
            }
          }, 500);
        }
      };
      
      recognition.onend = () => {
        if (voiceChatMode) {
          setTimeout(() => {
            try { recognition.start(); } catch(e) {}
          }, 100);
        }
      };
      
      recognitionRef.current = recognition;
      
      if (voiceChatMode) {
        try { recognition.start(); setIsListening(true); } catch(e) {}
      }
    }
    
    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch(e) {}
      }
    };
  }, [voiceChatMode]);

  // API Functions
  const loadPersonalities = async () => {
    try {
      const res = await axios.get(`${API}/chat/personalities`);
      setPersonalities(res.data.personalities);
    } catch (error) {
      console.error("Failed to load personalities:", error);
    }
  };

  const loadConversations = async () => {
    try {
      const response = await axios.get(`${API}/chat/conversations`);
      setConversations(response.data.conversations || []);
    } catch (error) {
      setConversations([]);
    }
  };

  const loadMessages = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/chat/conversations/${conversationId}/messages`);
      const serverMsgs = response.data.messages || [];
      setMessages(prev => {
        // Guard against a slow/stale fetch (fired when a brand-new conversation
        // is created mid-send) clobbering the optimistic + streamed messages we
        // already show for the SAME conversation. Without this the reload races
        // the stream → messages flicker/disappear or get duplicated.
        const prevSameConv = prev.length > 0 && prev[prev.length - 1]?.conversation_id === conversationId;
        if (prevSameConv && prev.length > serverMsgs.length) {
          return prev;
        }
        return serverMsgs;
      });
    } catch (error) {
      console.error("Failed to load messages:", error);
    }
  };

  const loadAdminData = async () => {
    try {
      const [dashboardRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`),
        axios.get(`${API}/admin/users/search`)
      ]);
      setAdminDashboard(dashboardRes.data);
      setAdminUsers(usersRes.data.users);
    } catch (error) {
      toast.error("Admin verileri yüklenemedi");
    }
  };

  const loadUserMemory = async () => {
    try {
      const res = await axios.get(`${API}/memory`);
      setUserMemory(res.data.memory);
    } catch (error) {
      console.error("Failed to load memory:", error);
    }
  };

  const loadCredits = async () => {
    try {
      const res = await axios.get(`${API}/credits`);
      setCredits(res.data.credits);
      setIsUnlimitedCredits(res.data.is_unlimited || false);
    } catch (error) {
      console.error("Failed to load credits:", error);
    }
  };

  const loadCreditPackages = async () => {
    try {
      const res = await axios.get(`${API}/payments/packages`);
      setCreditPackages(res.data.packages || []);
    } catch (error) {
      console.error("Failed to load packages:", error);
    }
  };

  const loadSubscriptionPlans = async () => {
    try {
      const res = await axios.get(`${API}/subscriptions/plans`);
      setSubscriptionPlans(res.data.plans || []);
    } catch (error) {
      console.error("Failed to load subscription plans:", error);
    }
  };

  const loadSubscriptionStatus = async () => {
    try {
      const res = await axios.get(`${API}/subscriptions/status`);
      setUserSubscription(res.data.subscription);
    } catch (error) {
      console.error("Failed to load subscription status:", error);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 10) {
      toast.error("Ödeme durumu kontrol edilemedi.");
      return;
    }
    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        setPaymentSuccess({ credits: res.data.credits_added, balance: res.data.new_balance });
        setCredits(res.data.new_balance);
        return;
      }
      if (res.data.status === "expired") {
        toast.error("Ödeme süresi doldu");
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  // Memory functions
  const extractMemoryFromConversation = async () => {
    if (!activeConversation) return;
    try {
      const res = await axios.post(`${API}/memory/extract`, { conversation_id: activeConversation.id });
      setUserMemory(res.data.memory);
    } catch (error) {
      toast.error("Bilgi çıkarılamadı");
    }
  };

  // Custom Assistants functions
  const loadCustomAssistants = async () => {
    try {
      const res = await axios.get(`${API}/assistants`);
      setCustomAssistants(res.data.assistants || []);
    } catch (error) {
      console.error("Failed to load custom assistants:", error);
    }
  };

  const handleAssistantCreated = (assistant) => {
    loadCustomAssistants();
  };

  const handleAssistantDeleted = (assistantId) => {
    if (selectedCustomAssistant?.id === assistantId) {
      setSelectedCustomAssistant(null);
      setSelectedAssistant("kaan");
      setSelectedVoice("onyx");
    }
    loadCustomAssistants();
  };

  const selectCustomAssistant = (assistant) => {
    setSelectedCustomAssistant(assistant);
    setSelectedAssistant(null); // Clear default assistant
    setSelectedVoice(assistant.voice);
  };

  const selectDefaultAssistant = (assistant) => {
    setSelectedCustomAssistant(null); // Clear custom assistant
    setSelectedAssistant(assistant);
    setSelectedVoice(assistant === "kaan" ? "onyx" : "nova");
  };

  // RAG functions
  const loadRagDocuments = async () => {
    try {
      const res = await axios.get(`${API}/tools/rag/documents`);
      setRagDocuments(res.data.documents || []);
    } catch (error) {
      console.error("Failed to load RAG documents:", error);
    }
  };

  const uploadRagDocument = async (file) => {
    if (!file) return;
    setIsUploadingDocument(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/tools/rag/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      loadRagDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Doküman yüklenemedi");
    } finally {
      setIsUploadingDocument(false);
    }
  };

  const deleteRagDocument = async (documentId) => {
    try {
      await axios.delete(`${API}/tools/rag/documents/${documentId}`);
      loadRagDocuments();
    } catch (error) {
      toast.error("Doküman silinemedi");
    }
  };

  const addFact = async () => {
    if (!newFact.trim()) return;
    try {
      const res = await axios.post(`${API}/memory/update`, { facts: [newFact] });
      setUserMemory(res.data.memory);
      setNewFact("");
    } catch (error) {
      toast.error("Eklenemedi");
    }
  };

  const addInterest = async () => {
    if (!newInterest.trim()) return;
    try {
      const res = await axios.post(`${API}/memory/update`, { interests: [newInterest] });
      setUserMemory(res.data.memory);
      setNewInterest("");
    } catch (error) {
      toast.error("Eklenemedi");
    }
  };

  const saveBirthday = async () => {
    if (!birthdayDate) return;
    try {
      await axios.post(`${API}/memory/date`, { date_type: "birthday", date: birthdayDate, description: "Doğum günü" });
      loadUserMemory();
      setBirthdayDate("");
    } catch (error) {
      toast.error("Kaydedilemedi");
    }
  };

  const clearMemory = async () => {
    showConfirm({
      title: t('clear_memory_title'),
      message: t('clear_memory_message'),
      confirmText: t('yes_clear'),
      cancelText: t('cancel'),
      variant: "danger",
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/memory`);
          setUserMemory({ facts: [], preferences: {}, interests: [], important_dates: {}, last_topics: [] });
        } catch (error) {
          toast.error(t('clear_failed'));
        }
        setShowConfirmDialog(false);
      }
    });
  };

  // Payment functions
  const purchaseCredits = async (creditAmount) => {
    try {
      const res = await axios.post(`${API}/credits/add`, { amount: creditAmount });
      setCredits(res.data.new_balance);
      setShowCreditModal(false);
    } catch (error) {
      toast.error("Kredi eklenemedi");
    }
  };

  const purchaseWithStripe = async (packageId) => {
    if (!user) {
      toast.error("Lütfen önce giriş yapın");
      setShowAuthModal(true);
      return;
    }
    setIsProcessingPayment(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, { package_id: packageId, origin_url: window.location.origin });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      toast.error("Ödeme başlatılamadı");
      setIsProcessingPayment(false);
    }
  };

  const transferCredits = async (email, amount) => {
    if (!email.trim() || amount <= 0) {
      toast.error("Geçerli e-posta ve miktar girin");
      return false;
    }
    try {
      const res = await axios.post(`${API}/credits/transfer`, { to_email: email, amount });
      setCredits(res.data.your_balance);
      setShowTransferModal(false);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Transfer başarısız");
      return false;
    }
  };

  const changePassword = async (currentPassword, newPassword, confirmPassword) => {
    if (!currentPassword || !newPassword) {
      toast.error("Tüm alanları doldurun");
      return false;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Yeni şifreler eşleşmiyor");
      return false;
    }
    if (newPassword.length < 6) {
      toast.error("Şifre en az 6 karakter olmalı");
      return false;
    }
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: currentPassword, new_password: newPassword });
      setShowProfileModal(false);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Şifre değiştirilemedi");
      return false;
    }
  };

  // Subscription functions
  const subscribeToplan = async (planId) => {
    if (!user) {
      toast.error("Lütfen önce giriş yapın");
      setShowAuthModal(true);
      return;
    }
    setIsProcessingPayment(true);
    try {
      const res = await axios.post(`${API}/subscriptions/subscribe`, { plan_id: planId, origin_url: window.location.origin });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Abonelik başlatılamadı");
      setIsProcessingPayment(false);
    }
  };

  const cancelSubscription = async () => {
    showConfirm({
      title: t('cancel_subscription_title'),
      message: t('cancel_subscription_message'),
      confirmText: t('yes_cancel'),
      cancelText: t('no_back'),
      variant: "danger",
      onConfirm: async () => {
        try {
          await axios.post(`${API}/subscriptions/cancel`);
          setUserSubscription(null);
          toast.success(t('subscription_cancelled'));
        } catch (error) {
          toast.error(t('cancel_failed'));
        }
        setShowConfirmDialog(false);
      }
    });
  };

  // Voice functions
  const toggleVoiceChatMode = () => {
    if (!voiceChatMode) {
      setVoiceChatMode(true);
      setIsListening(true);
      setVoiceEnabled(true);
      toast.success(t('voice_chat_active'));
    } else {
      setVoiceChatMode(false);
      setIsListening(false);
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch(e) {}
      }
      toast.info(t('voice_chat_off'));
    }
  };

  // Metni cümlelere ayır (Türkçe kısaltmaları ve ondalık sayıları koruyarak)
  const splitIntoSentences = (raw) => {
    if (!raw) return [];
    // Bilinen kısaltmalar — nokta yanlışlıkla cümle sonu sayılmasın
    const ABBR = ["vb", "vs", "vd", "bkz", "örn", "orn", "Dr", "Prof", "Doç", "Doc",
      "Av", "Sn", "No", "Tel", "Mah", "Cad", "Sok", "Apt", "yy", "Mö", "Ms",
      "TL", "ABD", "Bkz", "Örn", "Yrd", "Uzm", "Op", "St", "Mr", "Mrs", "Ms",
      "pl", "min", "max", "yak", "age", "bkz"];
    let txt = String(raw).trim();
    // Kısaltmalardaki noktayı geçici bir işaretle değiştir
    ABBR.forEach((a) => {
      const re = new RegExp(`(^|[\\s(])${a}\\.`, "g");
      txt = txt.replace(re, `$1${a}<DOT>`);
    });
    // Ondalık / binlik sayılardaki noktayı koru (3.14, 1.500)
    txt = txt.replace(/(\d)\.(\d)/g, "$1<DOT>$2");
    // Cümle sonu noktalama + boşluk → böl
    const parts = txt
      .split(/(?<=[.!?…])\s+/)
      .map((s) => s.replace(/<DOT>/g, ".").trim())
      .filter(Boolean);
    // Çok uzun (noktalamasız) parçaları ~280 karakterde böl
    const out = [];
    parts.forEach((p) => {
      if (p.length <= 320) { out.push(p); return; }
      let rest = p;
      while (rest.length > 320) {
        let cut = rest.lastIndexOf(",", 300);
        if (cut < 120) cut = rest.lastIndexOf(" ", 300);
        if (cut < 120) cut = 300;
        out.push(rest.slice(0, cut + 1).trim());
        rest = rest.slice(cut + 1).trim();
      }
      if (rest) out.push(rest);
    });
    return out;
  };

  // Tek bir ses parçasını (base64 mp3) çal, bittiğinde resolve et
  const playAudioChunk = (base64) =>
    new Promise((resolve) => {
      try {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const audioBlob = new Blob([new Uint8Array(byteNumbers)], { type: "audio/mpeg" });
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);
        audio.crossOrigin = "anonymous";
        currentAudioRef.current = audio;

        // Waveform analyser (her parça için yeniden kurulur)
        try {
          if (!audioContextRef.current) {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            audioContextRef.current = new Ctx();
          }
          const audioCtx = audioContextRef.current;
          if (audioCtx.state === "suspended") audioCtx.resume();
          const source = audioCtx.createMediaElementSource(audio);
          const analyser = audioCtx.createAnalyser();
          analyser.fftSize = 128;
          analyser.smoothingTimeConstant = 0.7;
          source.connect(analyser);
          analyser.connect(audioCtx.destination);
          analyserRef.current = analyser;
          audioSourceRef.current = source;
        } catch (waErr) {
          console.warn("Waveform analyser setup skipped:", waErr);
        }

        const cleanup = () => {
          URL.revokeObjectURL(audioUrl);
          resolve();
        };
        audio.onended = cleanup;
        audio.onerror = cleanup;
        audio.play().catch((err) => {
          console.error("TTS Play error:", err);
          cleanup();
        });
      } catch (e) {
        console.error("playAudioChunk error:", e);
        resolve();
      }
    });

  const speakText = async (text) => {
    if (!voiceEnabled || isSpeaking) return;
    const sentences = splitIntoSentences((text || "").substring(0, 4000));
    if (sentences.length === 0) return;
    try {
      ttsAbortRef.current = false;
      setIsSpeaking(true);
      for (let i = 0; i < sentences.length; i++) {
        if (ttsAbortRef.current) break;
        let audioB64 = null;
        try {
          const response = await axios.post(`${API}/chat/tts`, {
            text: sentences[i],
            voice: selectedVoice,
            count_quota: i === 0, // Tüm cümleler tek TTS hakkı sayılır
          });
          audioB64 = response.data?.audio_base64;
        } catch (err) {
          if (i === 0) throw err; // ilk cümle (kota vs.) hatası → kullanıcıya bildir
          console.warn("TTS sentence skipped:", err?.message);
          continue;
        }
        if (ttsAbortRef.current) break;
        if (!audioB64) continue;
        await playAudioChunk(audioB64);
        // Cümleler arası doğal duraklama
        if (!ttsAbortRef.current && i < sentences.length - 1) {
          await new Promise((r) => setTimeout(r, 200));
        }
      }
    } catch (error) {
      console.error("TTS request error:", error);
      toast.error(t('audio_create_failed'));
    } finally {
      setIsSpeaking(false);
      currentAudioRef.current = null;
      analyserRef.current = null;
      audioSourceRef.current = null;
    }
  };

  // Sesi durdur
  const stopSpeaking = () => {
    ttsAbortRef.current = true;
    if (currentAudioRef.current) {
      try {
        currentAudioRef.current.pause();
        currentAudioRef.current.currentTime = 0;
      } catch (e) {}
      currentAudioRef.current = null;
    }
    analyserRef.current = null;
    audioSourceRef.current = null;
    setIsSpeaking(false);
  };

  // Whisper API tabanlı yüksek doğruluklu ses tanıma (browser Web Speech API yerine)
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);

  // Live mic level (0..1) → drives the InputArea recording visualizer
  const [micLevel, setMicLevel] = useState(0);
  // Live 12-band FFT for equalizer bars
  const [micBands, setMicBands] = useState(() => new Array(12).fill(0));
  const bandsSmoothRef = useRef(new Array(12).fill(0));
  const micAnalyserCtxRef = useRef(null);
  const micRafRef = useRef(null);

  const stopMicMeter = useCallback(() => {
    if (micRafRef.current) cancelAnimationFrame(micRafRef.current);
    micRafRef.current = null;
    if (micAnalyserCtxRef.current && micAnalyserCtxRef.current.state !== "closed") {
      micAnalyserCtxRef.current.close().catch(() => {});
    }
    micAnalyserCtxRef.current = null;
    setMicLevel(0);
    setMicBands(new Array(12).fill(0));
    bandsSmoothRef.current = new Array(12).fill(0);
  }, []);

  const startRecording = async () => {
    try {
      // Yüksek kaliteli mikrofon ayarları: gürültü/echo temizleme + otomatik gain
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
          sampleRate: 48000
        }
      });
      audioStreamRef.current = stream;

      // Canlı ses seviyesi ölçümü (kullanıcı konuştuğunu görsün)
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        // iOS Safari: AudioContext başlangıçta suspended → resume gerekli,
        // aksi halde AnalyserNode sıfır sample döndürür ve EQ oynamaz.
        if (ctx.state === "suspended") {
          try { await ctx.resume(); } catch { /* noop */ }
        }
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.75;
        source.connect(analyser);
        micAnalyserCtxRef.current = ctx;
        const timeBuf = new Uint8Array(analyser.fftSize);
        const freqBuf = new Uint8Array(analyser.frequencyBinCount);
        const BANDS = 12;
        // Focus on the voice range: only use first ~40% of bins (~0-8kHz)
        const usableBins = Math.floor(freqBuf.length * 0.4);
        const perBand = Math.max(1, Math.floor(usableBins / BANDS));

        const tick = () => {
          if (!micAnalyserCtxRef.current) return;

          // 1) RMS for overall level (badge / ripple compatibility)
          analyser.getByteTimeDomainData(timeBuf);
          let sum = 0;
          for (let i = 0; i < timeBuf.length; i++) {
            const v = (timeBuf[i] - 128) / 128;
            sum += v * v;
          }
          const rms = Math.sqrt(sum / timeBuf.length);
          setMicLevel(Math.min(1, rms * 6));

          // 2) 12-band FFT equalizer
          analyser.getByteFrequencyData(freqBuf);
          const nextBands = new Array(BANDS).fill(0);
          for (let b = 0; b < BANDS; b++) {
            let acc = 0;
            const start = b * perBand;
            for (let i = 0; i < perBand; i++) acc += freqBuf[start + i] || 0;
            const avg = acc / perBand / 255;         // 0..1
            const boosted = Math.min(1, avg * 1.6);  // boost quiet voices
            // exponential smoothing per band → "elastic" bounce
            const prev = bandsSmoothRef.current[b] || 0;
            const smooth = boosted > prev ? boosted : prev * 0.7 + boosted * 0.3;
            bandsSmoothRef.current[b] = smooth;
            nextBands[b] = smooth;
          }
          setMicBands(nextBands);

          micRafRef.current = requestAnimationFrame(tick);
        };
        tick();
      } catch (e) {
        console.warn("mic meter unavailable", e);
      }

      // Tarayıcının en iyi desteklediği formatı seç
      const mimeCandidates = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/mp4'
      ];
      let mimeType = '';
      for (const m of mimeCandidates) {
        if (window.MediaRecorder && MediaRecorder.isTypeSupported(m)) { mimeType = m; break; }
      }

      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 128000 })
        : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onerror = (e) => {
        console.error("MediaRecorder error:", e);
        toast.error(t('voice_recognition_error'));
        setIsRecording(false);
        stopMicMeter();
      };

      // 250ms aralıklarla veri topla → daha güvenilir
      recorder.start(250);
      setIsRecording(true);
      toast.info(t('voice_start_speaking'));
    } catch (error) {
      console.error("Microphone access error:", error);
      if (error.name === 'NotAllowedError') {
        toast.error(t('microphone_denied_settings'));
      } else {
        toast.error(t('voice_recognition_start_failed') + ": " + error.message);
      }
      setIsRecording(false);
      stopMicMeter();
    }
  };

  const stopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      setIsRecording(false);
      return;
    }

    recorder.onstop = async () => {
      // Mikrofonu ve seviye ölçerini bırak
      stopMicMeter();
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(tr => tr.stop());
        audioStreamRef.current = null;
      }

      const chunks = audioChunksRef.current;
      audioChunksRef.current = [];
      if (!chunks.length) {
        setIsRecording(false);
        return;
      }

      const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
      if (blob.size < 800) {
        // Çok kısa kayıt (< ~0.5s)
        setIsRecording(false);
        toast.info(t('voice_not_detected'));
        return;
      }

      const ext = (recorder.mimeType || 'audio/webm').includes('mp4') ? 'mp4'
                : (recorder.mimeType || '').includes('ogg') ? 'ogg' : 'webm';
      const formData = new FormData();
      formData.append('file', blob, `recording.${ext}`);
      if (currentLang) formData.append('language', currentLang);

      try {
        setIsTranscribing(true);
        const res = await axios.post(`${API}/chat/stt`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 30000
        });
        const text = (res.data?.text || '').trim();
        if (text) {
          setInputValue(prev => (prev ? prev + ' ' : '') + text);
          toast.success(t('voice_recognized_success'));
        } else {
          toast.info(t('voice_not_detected'));
        }
      } catch (error) {
        console.error('STT error:', error);
        toast.error(t('voice_recognition_error'));
      } finally {
        setIsRecording(false);
        setIsTranscribing(false);
      }
    };

    try {
      recorder.stop();
    } catch (e) {
      console.error('recorder.stop error:', e);
      setIsRecording(false);
    }
  };

  // Admin functions
  const searchAdminUsers = async (query = "") => {
    try {
      const res = await axios.get(`${API}/admin/users/search?q=${encodeURIComponent(query)}`);
      setAdminUsers(res.data.users);
    } catch (error) {
      console.error("Failed to search users:", error);
    }
  };

  const giftCreditsToUser = async (userId, amount) => {
    if (!amount || amount <= 0) {
      toast.error("Geçerli bir miktar girin");
      return;
    }
    try {
      await axios.post(`${API}/admin/users/${userId}/gift-credits`, { user_id: userId, amount, reason: "Admin tarafından hediye" });
      searchAdminUsers("");
      loadAdminData();
    } catch (error) {
      toast.error("Kredi hediye edilemedi");
    }
  };

  // Chat functions
  const createNewConversation = async () => {
    try {
      const title = t('new_chat_title') || 'Yeni Sohbet';
      const response = await axios.post(`${API}/chat/conversations`, { title });
      setConversations([response.data, ...conversations]);
      setActiveConversation(response.data);
      setMessages([]);
      setIsSidebarOpen(false);
    } catch (error) {
      toast.error(t('error_occurred') || "Yeni sohbet oluşturulamadı");
    }
  };

  // Anasayfaya dön - use chatGoHome from ChatContext
  const goHome = chatGoHome;

  const deleteConversation = async (e, conversationId) => {
    if (e) e.stopPropagation();
    try {
      await axios.delete(`${API}/chat/conversations/${conversationId}`);
      setConversations(conversations.filter(c => c.id !== conversationId));
      if (activeConversation?.id === conversationId) {
        setActiveConversation(null);
        setMessages([]);
      }
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };

  const renameConversation = async (conversationId, newTitle) => {
    try {
      await axios.put(`${API}/chat/conversations/${conversationId}/rename?title=${encodeURIComponent(newTitle)}`);
      setConversations(conversations.map(c => 
        c.id === conversationId ? { ...c, title: newTitle } : c
      ));
      if (activeConversation?.id === conversationId) {
        setActiveConversation({ ...activeConversation, title: newTitle });
      }
    } catch (error) {
      console.error("Rename failed:", error);
    }
  };

  const archiveConversation = async (conversationId) => {
    try {
      const res = await axios.put(`${API}/chat/conversations/${conversationId}/archive`);
      const isArchived = res.data.is_archived;
      setConversations(conversations.map(c => 
        c.id === conversationId ? { ...c, is_archived: isArchived } : c
      ));
    } catch (error) {
      console.error("Archive failed:", error);
    }
  };

  const shareConversation = async (conversationId) => {
    try {
      const res = await axios.post(`${API}/chat/conversations/${conversationId}/share`);
      return res.data.share_link;
    } catch (error) {
      console.error("Share failed:", error);
      return "";
    }
  };

  const exportConversation = async () => {
    if (!activeConversation) return;
    try {
      const response = await axios.get(`${API}/chat/conversations/${activeConversation.id}/export`);
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', `kaan-sohbet-${activeConversation.id}.json`);
      linkElement.click();
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  // Spell correction function
  const correctSpelling = async (text) => {
    try {
      const formData = new FormData();
      formData.append('text', text);
      const response = await axios.post(`${API}/chat/correct-spelling`, formData);
      return response.data.corrected || text;
    } catch (error) {
      console.error("Spell correction error:", error);
      return text;
    }
  };

  // Send message with streaming
  const sendMessage = useCallback(async (e = null, voiceInput = null) => {
    let messageToSend = voiceInput || inputValue;
    if (!messageToSend.trim() || isLoading) return;

    // Apply spell correction if enabled
    const settings = JSON.parse(localStorage.getItem("kaan-ai-settings") || "{}");
    if (settings.autoCorrect) {
      const corrected = await correctSpelling(messageToSend);
      if (corrected !== messageToSend) {
        messageToSend = corrected;
      }
    }

    // 💱 Natural-language Solana swap — DISABLED (feature hidden at user's request).
    // eslint-disable-next-line no-constant-condition
    if (false && /\b(sol|usdc|usdt)\b/i.test(messageToSend) && /\d/.test(messageToSend) &&
        /(swap|takas|çevir|cevir|dönüştür|donustur|bozdur|\byap\b|\bal\b|\bsat\b)/i.test(messageToSend)) {
      try {
        const { data } = await axios.post(`${API}/solana/swap/intent`, { text: messageToSend });
        if (data?.is_swap) {
          setInputValue("");
          if (data.over_cap) {
            toast.error(`Güvenlik limiti: işlem başına en fazla ${data.max_usd} USD. Lütfen daha küçük bir miktar söyle.`);
          }
          setSwapPrefill({
            fromMint: data.inputMint,
            toMint: data.outputMint,
            amount: data.amount ? String(data.amount) : "",
            autoExecute: !!data.amount && !data.over_cap,
          });
          if (!localStorage.getItem("sol_wallet")) {
            toast.message("Önce Phantom cüzdanını bağla");
          }
          setShowPhantom(true);
          return;
        }
      } catch (e) { /* not a swap → fall through to normal chat */ }
    }

    let convId = activeConversation?.id;
    if (!convId) {
      try {
        const response = await axios.post(`${API}/chat/conversations`, { title: "Yeni Sohbet" });
        setConversations(prev => [response.data, ...prev]);
        setActiveConversation(response.data);
        convId = response.data.id;
      } catch (error) {
        toast.error("Sohbet başlatılamadı");
        return;
      }
    }

    const userMessage = messageToSend;
    setInputValue("");
    setIsLoading(true);
    setStreamingText("");

    const tempMsgId = `temp-${Date.now()}`;
    const tempUserMsg = {
      id: tempMsgId,
      conversation_id: convId,
      role: "user",
      content: userMessage,
      type: "text",
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);

    // Track streamed text at outer scope so finally-block safety net can
    // recover the assistant reply if the SSE connection drops before we
    // receive `data.done: true` (mobile signal, timeout, ...).
    let fullTextSoFar = "";
    let streamCompletedRef = false;

    try {
      if (useStreaming) {
        const response = await fetch(`${API}/chat/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(localStorage.getItem("kaan_token") && { 'Authorization': `Bearer ${localStorage.getItem("kaan_token")}` })
          },
          body: JSON.stringify({ 
            conversation_id: convId, 
            message: userMessage, 
            personality: selectedPersonality,
            assistant: selectedAssistant,
            custom_assistant_id: selectedCustomAssistant?.id || null,
            language: currentLang,
            model_mode: (() => {
              try {
                return JSON.parse(localStorage.getItem("kaan-ai-settings") || "{}").modelMode || "auto";
              } catch { return "auto"; }
            })()
          })
        });

        // 🚦 Handle quota / credit errors BEFORE trying to read the SSE body.
        // A 429/402 returns a JSON error (no `data:` frames) which would
        // otherwise be silently swallowed by the reader loop → user sees
        // nothing happen. Surface a friendly, warm upgrade nudge instead.
        if (!response.ok) {
          let detail = null;
          try {
            const errJson = await response.json();
            detail = errJson?.detail;
          } catch (_) { /* non-JSON body */ }

          if (response.status === 429 && detail && typeof detail === "object" && detail.error === "msg_quota_exceeded") {
            // Logged-in user hit their friendly daily message allowance.
            toast(t("quota_msg_daily_warm"), {
              duration: 9000,
              icon: "💜",
              description: t("quota_msg_daily_warm_desc"),
              style: { minWidth: "440px", textAlign: "left" },
              action: {
                label: t("quota_upgrade_cta"),
                onClick: () => setShowSubscriptionModal(true),
              },
            });
          } else if (response.status === 429) {
            // Anonymous daily limit.
            toast.error(t("daily_limit_short"), { duration: 6000 });
            setAnonymousLimit(prev => ({ ...prev, remaining: 0 }));
            setShowAuthModal(true);
          } else if (response.status === 402) {
            toast.error(t("insufficient_credits"));
            setShowCreditModal(true);
          } else {
            toast.error(t("message_send_failed"));
          }
          setMessages(prev => prev.filter(m => m.id !== tempMsgId));
          setIsLoading(false);
          setStreamingText("");
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let fullText = "";
        let sseBuffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          // stream:true keeps multi-byte UTF-8 chars (ç, ş, ğ, Arabic, 中…) intact
          // across packet boundaries; buffer carries any partial trailing line.
          sseBuffer += decoder.decode(value, { stream: true });
          const lines = sseBuffer.split('\n');
          sseBuffer = lines.pop() ?? "";

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.chunk) {
                  fullText += data.chunk;
                  fullTextSoFar = fullText; // sync outer-scope tracker
                  setStreamingText(fullText);
                }
                if (data.done) {
                  streamCompletedRef = true; // mark so finally-block skips recovery
                  if (data.credits_remaining !== undefined && data.credits_remaining !== null) {
                    setCredits(data.credits_remaining);
                  }
                  // Update anonymous limit
                  if (data.anonymous_remaining !== undefined && data.anonymous_remaining !== null) {
                    const newRemaining = data.anonymous_remaining;
                    setAnonymousLimit(prev => {
                      // Uyarı göster
                      if (newRemaining === 0) {
                        toast.error(t('daily_limit_exceeded'), { duration: 6000 });
                        setShowAuthModal(true);
                      } else if (newRemaining === 1) {
                        toast.warning(t('last_free_question'), { duration: 4000 });
                      } else if (newRemaining === 2) {
                        toast.info(t('remaining_free_questions', { count: newRemaining }), { duration: 3000 });
                      }
                      return { ...prev, remaining: newRemaining, used: prev.limit - newRemaining };
                    });
                  }
                  // Çift mesaj hatasını önlemek için: sadece assistant mesajını ekle, user mesajı zaten eklendi
                  setMessages(prev => {
                    const assistantId = data.message_id || `assistant-${Date.now()}`;
                    // Geçici user mesajına gerçek ID ver
                    const withUser = prev.map(m => m.id === tempMsgId ? { ...m, id: `user-${Date.now()}` } : m);
                    // De-dupe: a racing loadMessages reload may have already
                    // injected this assistant message from the server.
                    if (withUser.some(m => m.role === "assistant" && m.id === assistantId)) {
                      return withUser;
                    }
                    return withUser.concat([
                      { id: assistantId, conversation_id: convId, role: "assistant", content: fullText, type: "text", created_at: new Date().toISOString() }
                    ]);
                  });
                  setStreamingText("");
                  if (voiceEnabled) speakText(fullText);
                  loadConversations();
                }
                if (data.error && data.error.includes("kredi")) {
                  toast.error(data.error);
                }
              } catch (e) {}
            }
          }
        }
      } else {
        const response = await axios.post(`${API}/chat`, { conversation_id: convId, message: userMessage, personality: selectedPersonality });
        if (response.data.credits_remaining !== undefined) {
          setCredits(response.data.credits_remaining);
        }
        streamCompletedRef = true; // non-stream path completes synchronously
        setMessages(prev => {
          const filtered = prev.filter(m => m.id !== "temp-user");
          return [...filtered,
            { ...tempUserMsg, id: Date.now().toString() },
            { id: response.data.message_id, conversation_id: convId, role: "assistant", content: response.data.response, type: "text", created_at: new Date().toISOString() }
          ];
        });
        if (voiceEnabled) speakText(response.data.response);
      }
      loadConversations();
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error(t('insufficient_credits'));
        setShowCreditModal(true);
      } else if (error.response?.status === 429) {
        const detail = error.response?.data?.detail;
        if (detail && typeof detail === "object" && detail.error === "msg_quota_exceeded") {
          toast(t("quota_msg_daily_warm"), {
            duration: 9000,
            icon: "💜",
            description: t("quota_msg_daily_warm_desc"),
            style: { minWidth: "440px", textAlign: "left" },
            action: { label: t("quota_upgrade_cta"), onClick: () => setShowSubscriptionModal(true) },
          });
        } else {
          // Anonymous limit reached
          toast.error(t('daily_limit_short'), { duration: 6000 });
          setAnonymousLimit(prev => ({ ...prev, remaining: 0 }));
          setShowAuthModal(true);
        }
      } else {
        toast.error(t('message_send_failed'));
      }
      setMessages(prev => prev.filter(m => m.id !== "temp-user"));
    } finally {
      // 🛡️ SAFETY NET: If the SSE stream never delivered `data.done: true`
      // (network glitch, mobile signal drop, timeout mid-answer) the assistant
      // reply exists only in `streamingText` and would vanish when we clear
      // it below. Commit whatever we streamed as a durable message so the
      // user never sees their answer typed then disappear.
      if (fullTextSoFar && !streamCompletedRef) {
        setMessages(prev => {
          // Don't duplicate if data.done already added it
          if (prev.some(m => m.role === "assistant" && m.content === fullTextSoFar)) return prev;
          return prev.map(m => m.id === tempMsgId ? { ...m, id: `user-${Date.now()}` } : m).concat([
            { id: `assistant-recovered-${Date.now()}`, conversation_id: convId, role: "assistant", content: fullTextSoFar, type: "text", created_at: new Date().toISOString() }
          ]);
        });
      }
      setIsLoading(false);
      setStreamingText("");
    }
  }, [activeConversation?.id, inputValue, isLoading, selectedPersonality, selectedAssistant, selectedCustomAssistant, useStreaming, voiceEnabled, currentLang]);

  // Voice-mode helper: run a full user→AI turn, return the AI reply text so
  // VoiceModeModal can pipe it into TTS. Reuses the streaming endpoint and
  // pushes messages into the visible chat as they arrive.
  const sendVoiceModeMessage = useCallback(async (userText, opts = {}) => {
    if (!userText?.trim()) return "";
    const { tone = "auto", onStreamChunk } = opts;
    // Read the LATEST active conversation from the ref (avoids stale closure —
    // a re-render between voice-mode turns would otherwise keep sending the
    // very first captured value, creating a new conversation every turn).
    let convId = activeConversationRef.current?.id;
    if (!convId) {
      try {
        const response = await axios.post(`${API}/chat/conversations`, { title: "Sesli Sohbet" });
        setConversations(prev => [response.data, ...prev]);
        setActiveConversation(response.data);
        activeConversationRef.current = response.data;
        convId = response.data.id;
      } catch (error) {
        toast.error(t('message_send_failed'));
        return "";
      }
    }
    const tempMsgId = `voice-user-${Date.now()}`;
    setMessages(prev => [...prev, {
      id: tempMsgId, conversation_id: convId, role: "user",
      content: userText, type: "text", created_at: new Date().toISOString()
    }]);
    setIsLoading(true);
    setStreamingText("");
    let fullText = "";
    // Build the actual message sent to backend: append a tone directive if the
    // user forced a specific tone. Display remains the original user text.
    const toneDirectives = {
      friendly: "\n\n[Ton talebi: Samimi, sıcak ve dostane bir üslupla, kısa cümlelerle yanıtla. Emoji ekleyebilirsin.]",
      balanced: "\n\n[Ton talebi: Dengeli, nötr ve profesyonel bir üslupla yanıtla. Ne fazla resmi, ne fazla samimi.]",
      formal:   "\n\n[Ton talebi: Resmi ve saygılı bir üslupla, 'siz' hitabıyla, profesyonel dille yanıtla. Emoji kullanma.]",
    };
    const outbound = tone && tone !== "auto" && toneDirectives[tone]
      ? userText + toneDirectives[tone]
      : userText;
    try {
      const response = await fetch(`${API}/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem("kaan_token") && { 'Authorization': `Bearer ${localStorage.getItem("kaan_token")}` })
        },
        body: JSON.stringify({
          conversation_id: convId,
          message: outbound,
          personality: selectedPersonality,
          assistant: selectedAssistant,
          custom_assistant_id: selectedCustomAssistant?.id || null,
          language: currentLang,
          model_mode: (() => {
            try { return JSON.parse(localStorage.getItem("kaan-ai-settings") || "{}").modelMode || "auto"; }
            catch { return "auto"; }
          })(),
          realtime: true
        })
      });
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let sseBuffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        sseBuffer += decoder.decode(value, { stream: true });
        const lines = sseBuffer.split('\n');
        sseBuffer = lines.pop() ?? "";
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.chunk) {
              fullText += data.chunk;
              setStreamingText(fullText);
              if (typeof onStreamChunk === "function") onStreamChunk(fullText);
            }
            if (data.done) {
              if (data.credits_remaining !== undefined && data.credits_remaining !== null) setCredits(data.credits_remaining);
              setMessages(prev => prev.concat([{
                id: data.message_id || `voice-ai-${Date.now()}`,
                conversation_id: convId, role: "assistant",
                content: fullText, type: "text",
                created_at: new Date().toISOString()
              }]));
              setStreamingText("");
              loadConversations();
            }
          } catch { /* noop */ }
        }
      }
    } catch (e) {
      console.error("voice-mode stream error", e);
      toast.error(t('message_send_failed'));
    } finally {
      setIsLoading(false);
    }
    return fullText;
  }, [selectedPersonality, selectedAssistant, selectedCustomAssistant, currentLang, t]);

  // Voice input effect
  const sendMessageRef = useRef(null);
  sendMessageRef.current = sendMessage;
  
  useEffect(() => {
    if (voiceChatMode && inputValue.trim() && !isLoading) {
      const timer = setTimeout(() => {
        if (sendMessageRef.current) {
          sendMessageRef.current(null, inputValue);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [inputValue, voiceChatMode, isLoading]);

  // Image functions
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('unsupported_format'));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('file_too_large_10mb'));
      return;
    }
    setUploadedImage(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
    setMode("image_analysis");
  };

  const cancelImageUpload = () => {
    setUploadedImage(null);
    setImagePreview(null);
    setSelectedFaces([]);
    setMode("chat");
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const analyzeImage = async () => {
    if (!uploadedImage || isAnalyzingImage) return;
    const question = inputValue.trim() || "Bu görseli analiz et ve açıkla.";
    setIsAnalyzingImage(true);
    
    let convId = activeConversation?.id;
    if (!convId) {
      try {
        const res = await axios.post(`${API}/chat/conversations`);
        setActiveConversation(res.data);
        convId = res.data.id;
        await loadConversations();
      } catch (err) {
        toast.error(t('chat_start_failed'));
        setIsAnalyzingImage(false);
        return;
      }
    }
    
    const tempUserMsg = {
      id: "temp-image",
      conversation_id: convId,
      role: "user",
      content: `🖼️ Görsel yüklendi\n\n${question}`,
      type: "image_analysis",
      created_at: new Date().toISOString(),
      image_preview: imagePreview
    };
    setMessages(prev => [...prev, tempUserMsg]);
    
    try {
      const formData = new FormData();
      formData.append("file", uploadedImage);
      formData.append("question", question);
      formData.append("conversation_id", convId);
      
      const res = await axios.post(`${API}/chat/analyze-image-upload`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      
      if (res.data.credits_remaining !== undefined) {
        setCredits(res.data.credits_remaining);
      }
      
      setMessages(prev => {
        const filtered = prev.filter(m => m.id !== "temp-image");
        return [...filtered,
          { ...tempUserMsg, id: Date.now().toString() },
          { id: Date.now().toString() + "-response", conversation_id: convId, role: "assistant", content: res.data.analysis, type: "text", created_at: new Date().toISOString() }
        ];
      });
      
      cancelImageUpload();
      setInputValue("");
      if (voiceEnabled) speakText(res.data.analysis);
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error(t('insufficient_credits'));
        setShowCreditModal(true);
      } else {
        toast.error(t('image_analysis_failed'));
      }
      setMessages(prev => prev.filter(m => m.id !== "temp-image"));
    } finally {
      setIsAnalyzingImage(false);
      loadConversations();
    }
  };

  // Edit the photo the user staged via the "+" attach menu, using the text
  // they typed as the edit instruction → routes to /chat/edit-image.
  const editUploadedImage = async () => {
    const hasPerPerson = selectedFaces.some((f) => (f.instruction || "").trim());
    if (!uploadedImage || isEditingImage || isAnalyzingImage) return;
    if (!inputValue.trim() && !hasPerPerson) return;
    setIsEditingImage(true);

    let convId = activeConversation?.id;
    if (!convId) {
      try {
        const response = await axios.post(`${API}/chat/conversations`, { title: "Görsel Düzenleme" });
        setConversations(prev => [response.data, ...prev]);
        setActiveConversation(response.data);
        convId = response.data.id;
      } catch (error) {
        toast.error(t('chat_start_failed'));
        setIsEditingImage(false);
        return;
      }
    }

    const prompt = inputValue.trim();
    const displayPrompt = prompt || (hasPerPerson
      ? selectedFaces.filter(f => (f.instruction || "").trim()).map(f => `${f.label}: ${f.instruction.trim()}`).join(" · ")
      : "");
    const preview = imagePreview;
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target.result;
      const tempUserMsg = {
        id: "temp-edit-upload",
        conversation_id: convId,
        role: "user",
        content: `✏️ Görsel düzenle: ${displayPrompt}`,
        type: "image_edit",
        created_at: new Date().toISOString(),
        image_preview: preview,
      };
      setMessages(prev => [...prev, tempUserMsg]);
      try {
        const response = await axios.post(`${API}/chat/edit-image`, {
          image_base64: base64,
          prompt: prompt,
          conversation_id: convId,
          face_regions: selectedFaces.length
            ? selectedFaces.map((f) => ({ box: f.box, label: f.label, instruction: f.instruction || undefined }))
            : undefined,
        });
        setMessages(prev => {
          const filtered = prev.filter(m => m.id !== "temp-edit-upload");
          return [...filtered,
            { ...tempUserMsg, id: Date.now().toString() },
            { id: Date.now().toString() + "-edited", conversation_id: convId, role: "assistant", content: response.data.image_url, type: "image", created_at: new Date().toISOString() }
          ];
        });
        if (response.data.credits_remaining !== undefined) {
          setCredits(response.data.credits_remaining);
        }
        toast.success(t('image_edit_success'));
        cancelImageUpload();
        setInputValue("");
        loadConversations();
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
      } catch (error) {
        if (error.response?.status === 402) {
          toast.error(t('insufficient_credits'));
          setShowCreditModal(true);
        } else {
          toast.error(t('image_edit_failed') + ": " + (error.response?.data?.detail || error.message));
        }
        setMessages(prev => prev.filter(m => m.id !== "temp-edit-upload"));
      } finally {
        setIsEditingImage(false);
      }
    };
    reader.readAsDataURL(uploadedImage);
  };

  const generateImage = async () => {
    if (!imagePrompt.trim() || isLoading) return;
    setIsLoading(true);
    
    let convId = activeConversation?.id;
    if (!convId) {
      try {
        const response = await axios.post(`${API}/chat/conversations`, { title: "Görsel Oluşturma" });
        setConversations([response.data, ...conversations]);
        setActiveConversation(response.data);
        convId = response.data.id;
      } catch (error) {
        toast.error(t('chat_start_failed'));
        setIsLoading(false);
        return;
      }
    }

    const prompt = imagePrompt;
    setImagePrompt("");

    const tempUserMsg = { id: "temp-img-user", conversation_id: convId, role: "user", content: `🖼️ Görsel oluştur: ${prompt}`, type: "image_request", created_at: new Date().toISOString() };
    setMessages(prev => [...prev, tempUserMsg]);
    // Scroll when user message is added
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);

    try {
      const response = await axios.post(`${API}/chat/generate-image`, { prompt, conversation_id: convId });
      setMessages(prev => {
        const filtered = prev.filter(m => m.id !== "temp-img-user");
        return [...filtered,
          { ...tempUserMsg, id: Date.now().toString() },
          { id: Date.now().toString() + "-img", conversation_id: convId, role: "assistant", content: response.data.image_url, type: "image", created_at: new Date().toISOString() }
        ];
      });
      loadConversations();
      // Switch to chat mode and scroll to see the result
      setMode("chat");
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error(t('insufficient_credits'));
        setShowCreditModal(true);
      } else {
        toast.error(t('image_create_failed'));
      }
      setMessages(prev => prev.filter(m => m.id !== "temp-img-user"));
    } finally {
      setIsLoading(false);
    }
  };

  // Image editing functions
  const handleEditImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Sadece JPEG, PNG veya WebP dosyaları yüklenebilir");
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      toast.error("Dosya boyutu 10MB'dan küçük olmalı");
      return;
    }
    
    setEditImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setEditImagePreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const editImage = async () => {
    if (!editImageFile || !editPrompt.trim() || isEditingImage) return;
    setIsEditingImage(true);
    
    let convId = activeConversation?.id;
    if (!convId) {
      try {
        const response = await axios.post(`${API}/chat/conversations`, { title: "Görsel Düzenleme" });
        setConversations([response.data, ...conversations]);
        setActiveConversation(response.data);
        convId = response.data.id;
      } catch (error) {
        toast.error(t('chat_start_failed'));
        setIsEditingImage(false);
        return;
      }
    }
    
    const prompt = editPrompt;
    
    // Convert file to base64
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target.result;
      
      const tempUserMsg = { id: "temp-edit-user", conversation_id: convId, role: "user", content: `✏️ Görsel düzenle: ${prompt}`, type: "image_edit", created_at: new Date().toISOString() };
      setMessages(prev => [...prev, tempUserMsg]);
      
      try {
        const response = await axios.post(`${API}/chat/edit-image`, {
          image_base64: base64,
          prompt: prompt,
          conversation_id: convId
        });
        
        setMessages(prev => {
          const filtered = prev.filter(m => m.id !== "temp-edit-user");
          return [...filtered,
            { ...tempUserMsg, id: Date.now().toString() },
            { id: Date.now().toString() + "-edited", conversation_id: convId, role: "assistant", content: response.data.image_url, type: "image", created_at: new Date().toISOString() }
          ];
        });
        
        if (response.data.credits_remaining !== undefined) {
          setCredits(response.data.credits_remaining);
        }
        
        toast.success(t('image_edit_success'));
        setEditPrompt("");
        setEditImageFile(null);
        setEditImagePreview(null);
        loadConversations();
        // Switch to chat mode and scroll to see the result
        setMode("chat");
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
      } catch (error) {
        if (error.response?.status === 402) {
          toast.error(t('insufficient_credits'));
          setShowCreditModal(true);
        } else {
          toast.error(t('image_edit_failed') + ": " + (error.response?.data?.detail || error.message));
        }
        setMessages(prev => prev.filter(m => m.id !== "temp-edit-user"));
      } finally {
        setIsEditingImage(false);
      }
    };
    reader.readAsDataURL(editImageFile);
  };

  const analyzeFile = async () => {
    if (!selectedFile || isLoading) return;
    setIsLoading(true);

    let convId = activeConversation?.id;
    if (!convId) {
      try {
        const response = await axios.post(`${API}/chat/conversations`, { title: "Dosya Analizi" });
        setConversations([response.data, ...conversations]);
        setActiveConversation(response.data);
        convId = response.data.id;
      } catch (error) {
        toast.error(t('chat_start_failed'));
        setIsLoading(false);
        return;
      }
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("conversation_id", convId);
    formData.append("question", fileQuestion || "Bu dosyayı analiz et ve özetle.");
    formData.append("language", currentLang);

    const tempUserMsg = { id: "temp-file-user", conversation_id: convId, role: "user", content: `📄 Dosya: ${selectedFile.name}\n\n${fileQuestion || "Bu dosyayı analiz et ve özetle."}`, type: "file", created_at: new Date().toISOString() };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      const response = await axios.post(`${API}/chat/analyze-file`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setMessages(prev => {
        const filtered = prev.filter(m => m.id !== "temp-file-user");
        return [...filtered,
          { ...tempUserMsg, id: Date.now().toString() },
          { id: Date.now().toString() + "-analysis", conversation_id: convId, role: "assistant", content: response.data.analysis, type: "text", created_at: new Date().toISOString() }
        ];
      });
      setSelectedFile(null);
      setFileQuestion("");
      loadConversations();
    } catch (error) {
      toast.error(t('file_analysis_failed'));
      setMessages(prev => prev.filter(m => m.id !== "temp-file-user"));
    } finally {
      setIsLoading(false);
    }
  };

  // Auth handler
  const handleAuth = async (e) => {
    e.preventDefault();
    try {
      if (authMode === "login") {
        await login(authForm.email, authForm.password);
      } else {
        await register(authForm.email, authForm.password, authForm.name);
      }
      setShowAuthModal(false);
      setAuthForm({ email: "", password: "", name: "", newPassword: "" });
      loadConversations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (mode === "chat") sendMessage();
      else if (mode === "image") generateImage();
    }
  };

  return (
    <div className="app-container">
      <Toaster 
        position="top-center" 
        richColors
        expand={true}
        duration={3000}
        toastOptions={{ 
          style: { 
            background: 'var(--bg-secondary)', 
            border: '2px solid var(--border-color)', 
            color: 'var(--text-primary)',
            borderRadius: '16px',
            padding: '20px 28px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            fontSize: '15px',
            fontWeight: '500',
            minWidth: '300px',
            textAlign: 'center'
          },
          className: 'toast-custom'
        }} 
      />
      
      {/* Modals */}
      <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} authMode={authMode} setAuthMode={setAuthMode} onSubmit={handleAuth} authForm={authForm} setAuthForm={setAuthForm} />
      <CreditModal open={showCreditModal} onOpenChange={setShowCreditModal} credits={credits} creditPackages={creditPackages} onPurchaseWithStripe={purchaseWithStripe} onPurchaseDemo={purchaseCredits} isProcessingPayment={isProcessingPayment} />
      <PaymentSuccessModal paymentSuccess={paymentSuccess} onClose={() => setPaymentSuccess(null)} />
      <VideoStudioModal open={showVideoStudio} onOpenChange={setShowVideoStudio} onRequestUpgrade={() => { setShowVideoStudio(false); setShowSubscriptionModal(true); }} />
      <ExchangeModal open={showExchange} onOpenChange={setShowExchange} />
      <PhantomModal open={showPhantom} onOpenChange={setShowPhantom} prefill={swapPrefill} onPrefillConsumed={() => setSwapPrefill(null)} />
      <SubscriptionModal open={showSubscriptionModal} onOpenChange={setShowSubscriptionModal} subscriptionPlans={subscriptionPlans} userSubscription={userSubscription} onSubscribe={subscribeToplan} onCancel={cancelSubscription} isProcessingPayment={isProcessingPayment} />
      <AdminPanel open={showAdminPanel} onOpenChange={setShowAdminPanel} adminDashboard={adminDashboard} adminUsers={adminUsers} onSearchUsers={searchAdminUsers} onGiftCredits={giftCreditsToUser} />
      <AnalyticsPanel open={showAnalyticsPanel} onOpenChange={setShowAnalyticsPanel} />
      <MemoryPanel open={showMemoryPanel} onOpenChange={setShowMemoryPanel} userMemory={userMemory} activeConversation={activeConversation} onExtractMemory={extractMemoryFromConversation} onAddFact={addFact} onAddInterest={addInterest} onSaveBirthday={saveBirthday} onClearMemory={clearMemory} newFact={newFact} setNewFact={setNewFact} newInterest={newInterest} setNewInterest={setNewInterest} birthdayDate={birthdayDate} setBirthdayDate={setBirthdayDate} ragDocuments={ragDocuments} onUploadDocument={uploadRagDocument} onDeleteDocument={deleteRagDocument} onRefreshDocuments={loadRagDocuments} isUploadingDocument={isUploadingDocument} />
      <ConfirmDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog} title={confirmConfig.title} message={confirmConfig.message} confirmText={confirmConfig.confirmText} cancelText={confirmConfig.cancelText} variant={confirmConfig.variant} onConfirm={confirmConfig.onConfirm} />
      <ProfileModal 
        open={showProfileModal} 
        onOpenChange={setShowProfileModal} 
        user={user} 
        onChangePassword={changePassword}
        onUpdateProfile={(updatedUser) => setUser(updatedUser)}
        archivedConversations={archivedConversations}
        onUnarchiveConversation={archiveConversation}
        onDeleteArchivedConversation={(id) => deleteConversation(null, id)}
        onThemeChange={setCurrentTheme}
        onRequestUpgrade={() => {
          setShowProfileModal(false);
          setShowUpgradeModal(true);
        }}
      />
      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        subscriptionPlans={subscriptionPlans}
        onSubscribe={(planId) => {
          setShowUpgradeModal(false);
          subscribeToplan(planId);
        }}
        onViewAllPlans={() => {
          setShowUpgradeModal(false);
          setShowSubscriptionModal(true);
        }}
        isProcessingPayment={isProcessingPayment}
        user={user}
        onRequireLogin={() => {
          setShowUpgradeModal(false);
          setShowAuthModal(true);
        }}
      />
      <TransferModal open={showTransferModal} onOpenChange={setShowTransferModal} credits={credits} isUnlimitedCredits={isUnlimitedCredits} onTransfer={transferCredits} />
      <AssistantCreatorModal open={showAssistantCreator} onOpenChange={setShowAssistantCreator} onAssistantCreated={handleAssistantCreated} onAssistantDeleted={handleAssistantDeleted} />

      {/* Live audio waveform while TTS is speaking */}
      <SpeakingWaveform isSpeaking={isSpeaking} analyserRef={analyserRef} onStop={stopSpeaking} />

      {/* Deep Research modal + job view */}
      <DeepResearchModal
        open={showDeepResearchModal}
        onOpenChange={setShowDeepResearchModal}
        user={user}
        onJobStarted={(jobId) => {
          setDrCurrentJobId(jobId);
          setDrJobViewOpen(true);
        }}
        onShowLeaderboard={() => setShowLeaderboard(true)}
        onShowCustomAgents={() => setShowCustomAgents(true)}
      />
      <DeepResearchJobView
        open={drJobViewOpen}
        onOpenChange={setDrJobViewOpen}
        jobId={drCurrentJobId}
        authToken={localStorage.getItem('kaan_token')}
      />
      <AgentLeaderboard open={showLeaderboard} onOpenChange={setShowLeaderboard} />
      <DeepResearchJobsPanel
        open={showJobsPanel}
        onOpenChange={setShowJobsPanel}
        user={user}
        onSelectJob={(jobId) => {
          setDrCurrentJobId(jobId);
          setDrJobViewOpen(true);
        }}
      />
      <CustomAgentsModal
        open={showCustomAgents}
        onOpenChange={setShowCustomAgents}
      />
      
      {/* Sidebar Overlay */}
      {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />}

      {/* Sidebar */}
      <Sidebar
        sidebarOpen={isSidebarOpen}
        setSidebarOpen={setIsSidebarOpen}
        user={user}
        credits={credits}
        isUnlimitedCredits={isUnlimitedCredits}
        userSubscription={userSubscription}
        conversations={conversations}
        activeConversation={activeConversation}
        setActiveConversation={setActiveConversation}
        createNewConversation={createNewConversation}
        deleteConversation={deleteConversation}
        exportConversation={exportConversation}
        showConfirm={showConfirm}
        logout={() => { logout(); setShowConfirmDialog(false); }}
        setShowAuthModal={setShowAuthModal}
        setShowCreditModal={setShowCreditModal}
        setShowSubscriptionModal={setShowSubscriptionModal}
        setShowTransferModal={setShowTransferModal}
        setShowMemoryPanel={setShowMemoryPanel}
        setShowProfileModal={setShowProfileModal}
        setShowExchange={setShowExchange}
        setShowPhantom={setShowPhantom}
        setShowAdminPanel={setShowAdminPanel}
        setShowAnalyticsPanel={setShowAnalyticsPanel}
        setShowDeepResearchModal={setShowDeepResearchModal}
        setShowJobsPanel={setShowJobsPanel}
        loadAdminData={loadAdminData}
        renameConversation={renameConversation}
        archiveConversation={archiveConversation}
        shareConversation={shareConversation}
        currentTheme={currentTheme}
      />

      {/* Main Area */}
      <main className="main-area">
        <div className="mobile-header">
          <Button variant="ghost" size="icon" onClick={() => setIsSidebarOpen(true)} data-testid="menu-button"><Menu size={24} /></Button>
          <div className="flex items-center gap-2 ml-3"><Bot size={20} className="text-blue-500" /><span className="font-semibold text-[var(--text-primary)]">KaanGPT</span></div>
        </div>

        <ChatHeader
          activeConversation={activeConversation}
          selectedAssistant={selectedAssistant}
          selectedCustomAssistant={selectedCustomAssistant}
          goHome={goHome}
          closeConversation={goHome}
        />

        <div className="chat-messages" data-testid="chat-messages" ref={chatScrollRef}>
          <div className="messages-container">
            {!activeConversation && messages.length === 0 ? (
              <WelcomeScreen
                user={user}
                selectedAssistant={selectedAssistant}
                selectedCustomAssistant={selectedCustomAssistant}
                customAssistants={customAssistants}
                anonymousLimit={anonymousLimit}
                selectDefaultAssistant={selectDefaultAssistant}
                selectCustomAssistant={selectCustomAssistant}
                createNewConversation={createNewConversation}
                setShowAssistantCreator={setShowAssistantCreator}
                setShowAuthModal={setShowAuthModal}
                setAuthMode={setAuthMode}
                mode={mode}
                setMode={setMode}
              />
            ) : (
              <ChatMessages
                messages={messages}
                activeConversation={activeConversation}
                selectedAssistant={selectedAssistant}
                selectedCustomAssistant={selectedCustomAssistant}
                voiceEnabled={voiceEnabled}
                isSpeaking={isSpeaking}
                speakText={speakText}
                stopSpeaking={stopSpeaking}
                setMessages={setMessages}
                showConfirm={showConfirm}
                streamingText={streamingText}
                isLoading={isLoading}
                messagesEndRef={messagesEndRef}
              />
            )}
          </div>
        </div>

        {/* Aşağı in — scroll-to-latest button, shown when scrolled up */}
        {showScrollDown && (activeConversation || messages.length > 0) && (
          <button
            type="button"
            onClick={scrollToBottom}
            className="scroll-to-bottom-btn"
            data-testid="scroll-to-bottom-btn"
            aria-label={t('scroll_to_bottom') || 'En alta in'}
            title={t('scroll_to_bottom') || 'En alta in'}
          >
            <ArrowDown size={20} />
          </button>
        )}

        <InputArea
          mode={mode}
          setMode={setMode}
          inputValue={inputValue}
          setInputValue={setInputValue}
          imagePrompt={imagePrompt}
          setImagePrompt={setImagePrompt}
          imagePreview={imagePreview}
          uploadedImage={uploadedImage}
          cancelImageUpload={cancelImageUpload}
          voiceEnabled={voiceEnabled}
          setVoiceEnabled={setVoiceEnabled}
          isRecording={isRecording}
          isTranscribing={isTranscribing}
          startRecording={startRecording}
          stopRecording={stopRecording}
          isLoading={isLoading}
          isAnalyzingImage={isAnalyzingImage}
          sendMessage={sendMessage}
          generateImage={generateImage}
          analyzeImage={analyzeImage}
          editUploadedImage={editUploadedImage}
          selectedFaces={selectedFaces}
          setSelectedFaces={setSelectedFaces}
          onOpenVideoStudio={() => setShowVideoStudio(true)}
          handleKeyPress={handleKeyPress}
          handleImageSelect={handleImageSelect}
          selectedAssistant={selectedAssistant}
          selectedCustomAssistant={selectedCustomAssistant}
          textareaRef={textareaRef}
          imageInputRef={imageInputRef}
          imageEditMode={imageEditMode}
          setImageEditMode={setImageEditMode}
          editImageFile={editImageFile}
          setEditImageFile={setEditImageFile}
          editImagePreview={editImagePreview}
          setEditImagePreview={setEditImagePreview}
          editPrompt={editPrompt}
          setEditPrompt={setEditPrompt}
          isEditingImage={isEditingImage}
          editImage={editImage}
          handleEditImageSelect={handleEditImageSelect}
          editImageInputRef={editImageInputRef}
          selectedFile={selectedFile}
          setSelectedFile={setSelectedFile}
          fileQuestion={fileQuestion}
          setFileQuestion={setFileQuestion}
          analyzeFile={analyzeFile}
          fileInputRef={fileInputRef}
          openVoiceMode={() => {
            // 🍎 iOS Safari audio unlock: play a silent buffer INSIDE the
            // opening user-gesture so the browser marks the shared <audio>
            // element as "allowed to play". Otherwise, when TTS finishes
            // its network round-trip (record → whisper → LLM → TTS ≈ 5-10s
            // later), iOS considers the gesture expired and silences the
            // reply — user sees the orb switch to "speaking" but hears nothing.
            try {
              let el = window.__kaanTTSAudio;
              if (!el) {
                el = document.createElement("audio");
                el.setAttribute("playsinline", "true");
                el.setAttribute("webkit-playsinline", "true");
                el.preload = "auto";
                el.crossOrigin = null;
                document.body.appendChild(el);
                window.__kaanTTSAudio = el;
              }
              // 0.4s of silence as data URL (mp3-encoded)
              el.src = "data:audio/mp3;base64,//uQxAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAACcQCAgICAgICAgICAgICAgICAgICAgICAgICAgICAgP////////////////////////////////////////8AAAA5TEFNRTMuMTAwAc0AAAAAAAAAABQgJAUHQQAB4AAAAnGWMK9nAAAAAAAAAAAAAAAAAAAA//sQxAADwAABpAAAACAAADSAAAAETEFNRTMuMTAwVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV";
              const p = el.play();
              if (p && typeof p.catch === "function") p.catch(() => { /* silent — some browsers reject data: URI, non-fatal */ });
              // Pause immediately so the next real TTS starts clean
              setTimeout(() => { try { el.pause(); el.currentTime = 0; } catch { /* noop */ } }, 60);
            } catch { /* noop — audio unlock is best-effort */ }
            setVoiceModeOpen(true);
          }}
          micLevel={micLevel}
          micBands={micBands}
        />
        <VoiceModeModal
          open={voiceModeOpen}
          onClose={() => setVoiceModeOpen(false)}
          onSendUserMessage={sendVoiceModeMessage}
          currentLang={currentLang}
          ttsVoice={(selectedCustomAssistant?.voice) || (selectedAssistant === "kaan" ? "onyx" : "nova")}
          personality={selectedPersonality}
          assistantProfile={
            selectedCustomAssistant
              ? {
                  name: selectedCustomAssistant.name,
                  voice: selectedCustomAssistant.voice || "nova",
                  gradient: "from-fuchsia-500 to-purple-600",
                }
              : selectedAssistant === "kaan"
                ? { name: "Kaan", voice: "onyx", gradient: "from-blue-500 to-cyan-500" }
                : selectedAssistant === "defne"
                  ? { name: "Defne", voice: "nova", gradient: "from-pink-500 to-rose-500" }
                  : { name: "Maya", voice: "shimmer", gradient: "from-emerald-500 to-teal-500" }
          }
        />
      </main>
    </div>
  );
}

export default App;
